/**
 * Shared environment configuration for both admin and client portals
 * Centralized configuration for feature flags, API settings, and app behavior
 */

export const env = {
  // API Configuration
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '',

  // App Configuration
  appVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
  environment: (import.meta.env.VITE_ENV || 'development') as 'development' | 'staging' | 'production',

  // Session Configuration (shared across both portals)
  sessionTokenKey: 'taskeye_access_token',
  currentOrgKey: 'taskeye_current_org',
  sessionTimeout: 7 * 24 * 60 * 60 * 1000, // 7 days in milliseconds

  // OTP Configuration
  otpLength: 6,
  otpExpiryMinutes: 5,

  // Pagination
  defaultPageSize: 10,
  defaultOffset: 0,

  // Query Configuration
  queryStaleTime: 5 * 60 * 1000, // 5 minutes
  queryCacheTime: 10 * 60 * 1000, // 10 minutes

  // Analytics / error tracking (blank disables the SDK)
  posthogKey: import.meta.env.VITE_POSTHOG_KEY || '',
  posthogHost: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
  sentryDsn: import.meta.env.VITE_SENTRY_DSN || '',

  // Metabase public-link dashboard embedded on the Instant Tasks landing page
  // (blank falls back to the coded dashboard mock).
  metabaseInstantTasksUrl: import.meta.env.VITE_METABASE_INSTANT_TASKS_URL || '',
} as const;

// Type-safe environment variables
export type Environment = typeof env;

// Helper to check if we're in development mode
export const isDevelopment = env.environment === 'development';
export const isProduction = env.environment === 'production';
export const isStaging = env.environment === 'staging';

// Helper to log environment info (useful for debugging)
export const logEnvironmentInfo = (portal: 'admin' | 'client' = 'admin') => {
  if (isDevelopment) {
    console.group(`${portal.charAt(0).toUpperCase() + portal.slice(1)} Portal Environment`);
    console.log('Environment:', env.environment);
    console.log('API Base URL:', env.apiBaseUrl || '(empty - using relative paths)');
    console.log('App Version:', env.appVersion);
    console.log('Session Token Key:', env.sessionTokenKey);
    console.log('Org Key:', env.currentOrgKey);
    console.groupEnd();
  }
};
